/* deploy: 1778084922 — trump-card restored 2026-05-06
 * ============================================================================
 * Viori Personalizer 2.0 — V6 Filter Quiz Controller
 *
 * URL-driven 5-page quiz. Each selection writes the relevant filter param
 * using the V6 filter-controller's URL convention:
 *   ?shopping_mission=Woman           (in-quiz state mirror; redirect uses
 *                                      filter.p.m.custom.shopping_mission
 *                                      so Shopify filters server-side, since
 *                                      the V6 controller doesn't yet treat
 *                                      shopping_mission as a first-class pill)
 *   ?scalp_type=Oily,Normal           (CSV)
 *   ?benefit=Volume,Strength          (CSV)
 *   ?concern=Thinning,Damaged         (CSV)
 *   ?hair_likeness=Brown+Curly+Hair
 *
 * On submit, the visitor is redirected to /collections/all carrying the
 * accumulated query string.
 *
 * Grey-out logic: on every step transition we GET
 *   /collections/all?{currentParams}&section_id=cust-v6-filter-data
 * and parse the returned `data-v6-filter-defs` JSON to determine which
 * values still have product matches. Values with count===0 get dimmed.
 * ========================================================================== */

(function () {
  'use strict';

  // Per-section initializers — keyed by section element so re-init on
  // shopify:section:load is idempotent.
  var initialized = new WeakSet();

  // V6 URL param keys, indexed by step number.
  var STEP_PARAM = {
    1: 'shopping_mission',
    2: 'benefit',
    3: 'scalp_type',
    4: 'concern',
    5: 'hair_likeness'
  };
  var SINGLE_STEPS  = { 1: true, 5: true };
  var MULTI_STEPS   = { 2: true, 3: true, 4: true };

  /* ============================================================
     Capture step (page 6) helpers
     ------------------------------------------------------------
     FAIL OPEN. This step stands between the visitor and the
     recommendations they asked for, so a marketing call must never
     be the reason that trip does not happen: 2500ms timeout, one
     retry after 1000ms, then warn and redirect anyway.
     ============================================================ */
  var CAPTURE_FLAG     = 'viori_quiz_captured';
  var CALL_TIMEOUT_MS  = 2500;
  var RETRY_DELAY_MS   = 1000;
  var KLAVIYO_REVISION = '2025-04-15';

  // Same marker and storage tier as the shop quiz, so a visitor captured on
  // either surface is not asked twice.
  function alreadyCaptured() {
    try {
      if (window.VioriAttribution && typeof window.VioriAttribution.getFlag === 'function') {
        return window.VioriAttribution.getFlag(CAPTURE_FLAG) === '1';
      }
    } catch (e) { /* noop */ }
    return false;
  }
  function markCaptured() {
    try {
      if (window.VioriAttribution && typeof window.VioriAttribution.setFlag === 'function') {
        window.VioriAttribution.setFlag(CAPTURE_FLAG, '1');
      }
    } catch (e) { /* noop */ }
  }

  function isEmail(v) {
    return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  }
  // Same implementation as the popup and the shop quiz.
  function normalizePhone(v) {
    if (!v) return null;
    var digits = String(v).replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    if (digits.length >= 8 && digits.length <= 15) return '+' + digits;
    return null;
  }

  function putProp(out, key, val) {
    if (val === null || val === undefined) return;
    var str = String(val);
    if (!str) return;                 // never send empty strings
    out[key] = str;
  }

  function flattenFilters(fs) {
    var out = {};
    if (!fs) return out;
    Object.keys(fs).forEach(function (k) {
      var v = fs[k];
      out['filter_' + k] = Object.prototype.toString.call(v) === '[object Array]' ? v.join(',') : v;
    });
    return out;
  }

  function withTimeout(promise, label) {
    var timer;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error(label + ' timed out after ' + CALL_TIMEOUT_MS + 'ms'));
      }, CALL_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(function () { clearTimeout(timer); });
  }

  function postToKlaviyo(companyId, listId, profileAttrs) {
    if (!companyId || !listId) {
      return Promise.reject(new Error('Klaviyo company_id or list_id is missing'));
    }
    return withTimeout(fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'revision': KLAVIYO_REVISION,
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'V6 Quiz',
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      })
    }).then(function (res) {
      if (res.status === 202 || res.status === 200 || res.status === 204) return res;
      return res.text().then(function (t) {
        throw new Error('Klaviyo ' + res.status + ': ' + t.slice(0, 240));
      });
    }), 'Klaviyo subscription POST');
  }

  function postToKlaviyoWithRetry(companyId, listId, profileAttrs) {
    return postToKlaviyo(companyId, listId, profileAttrs).catch(function (err) {
      console.warn('[v6-quiz] Klaviyo attempt 1 failed — retrying once in ' + RETRY_DELAY_MS + 'ms:', err);
      return new Promise(function (resolve) { setTimeout(resolve, RETRY_DELAY_MS); })
        .then(function () { return postToKlaviyo(companyId, listId, profileAttrs); });
    });
  }

  // Attribution also rides on the cart so the order-paid webhook can read it
  // without depending on the Klaviyo call. Fire-and-forget with keepalive:
  // awaiting it would put a second timeout between the visitor and their
  // results, and a plain fetch would be cancelled by the redirect.
  function writeCartAttributes() {
    var snap = {};
    try {
      if (window.VioriAttribution && typeof window.VioriAttribution.snapshot === 'function') {
        snap = window.VioriAttribution.snapshot() || {};
      }
    } catch (e) { /* noop */ }

    var attrs = {};
    Object.keys(snap).forEach(function (k) { putProp(attrs, 'viori_' + k, snap[k]); });
    if (!Object.keys(attrs).length) return Promise.resolve();

    function post() {
      return withTimeout(fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true
      }), 'cart attribute write');
    }
    return post()
      .catch(function () {
        return new Promise(function (r) { setTimeout(r, RETRY_DELAY_MS); }).then(post);
      })
      .catch(function (err) {
        console.warn('[v6-quiz] cart attribute write failed:', err);
      });
  }

  function trackCapture(eventName, payload) {
    try {
      if (window.gtag) gtag('event', eventName, payload);
    } catch (e) { /* noop */ }
  }

  function splitCSV(s) {
    if (!s) return [];
    return s.split(',').map(function (v) { return v.trim(); }).filter(Boolean);
  }

  function readURLParams() {
    var p = new URLSearchParams(window.location.search);
    return {
      // Read both V6-short and Shopify-native form so an external link
      // landing on the quiz with filter.p.m.custom.shopping_mission=X
      // hydrates the same way as ?shopping_mission=X.
      shopping_mission: p.get('shopping_mission')
                        || p.get('filter.p.m.custom.shopping_mission')
                        || '',
      benefit:          splitCSV(p.get('benefit')),
      scalp_type:       splitCSV(p.get('scalp_type')),
      concern:          splitCSV(p.get('concern')),
      hair_likeness:    p.get('hair_likeness') || ''
    };
  }

  function writeURLParams(state) {
    var p = new URLSearchParams(window.location.search);

    if (state.shopping_mission)  p.set('shopping_mission', state.shopping_mission);
    else                         p.delete('shopping_mission');

    if (state.benefit && state.benefit.length)  p.set('benefit', state.benefit.join(','));
    else                                        p.delete('benefit');

    if (state.scalp_type && state.scalp_type.length)  p.set('scalp_type', state.scalp_type.join(','));
    else                                              p.delete('scalp_type');

    if (state.concern && state.concern.length)  p.set('concern', state.concern.join(','));
    else                                        p.delete('concern');

    if (state.hair_likeness)  p.set('hair_likeness', state.hair_likeness);
    else                      p.delete('hair_likeness');

    var qs = p.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    window.history.replaceState({}, '', url);
  }

  function buildFilterQuery(state) {
    var p = new URLSearchParams();
    // shopping_mission is emitted in Shopify-native form so the collection
    // page filters server-side. The V6 controller doesn't yet recognize
    // shopping_mission as a first-class pill; mixed-format URL is fine
    // because Shopify reads native and V6 reads short for the rest.
    if (state.shopping_mission)                        p.append('filter.p.m.custom.shopping_mission', state.shopping_mission);
    if (state.benefit && state.benefit.length)         p.set('benefit', state.benefit.join(','));
    if (state.scalp_type && state.scalp_type.length)   p.set('scalp_type', state.scalp_type.join(','));
    if (state.concern && state.concern.length)         p.set('concern', state.concern.join(','));
    if (state.hair_likeness)                           p.set('hair_likeness', state.hair_likeness);
    return p.toString();
  }

  // ────────────────────────────────────────────────────────────────────────
  // Shopify-native filter param builder.
  // Quiz state uses semantic keys (?benefit=Volume) but Shopify's server-side
  // filtering only honors native param names (filter.p.m.custom.shop_by_benefit).
  // The collection-page controller translates semantic → native on landing,
  // but the quiz fetches SRA directly so it must do the translation itself.
  // Mirrors buildShopifyFilterParams in cust-v6-filter-controller.js.
  // ────────────────────────────────────────────────────────────────────────
  function buildSRAQuery(state) {
    var p = new URLSearchParams();
    if (state.scalp_type && state.scalp_type.length) {
      state.scalp_type.forEach(function (v) { p.append('filter.p.m.custom.scalp_type', v); });
    }
    if (state.concern && state.concern.length) {
      state.concern.forEach(function (v) { p.append('filter.p.m.custom.shop_by_concern', v); });
    }
    if (state.benefit && state.benefit.length) {
      state.benefit.forEach(function (v) { p.append('filter.p.m.custom.shop_by_benefit', v); });
    }
    if (state.hair_likeness) {
      p.append('filter.p.m.custom.hair_likeness', state.hair_likeness);
    }
    if (state.shopping_mission) {
      p.append('filter.p.m.custom.shopping_mission', state.shopping_mission);
    }
    return p.toString();
  }

  // ────────────────────────────────────────────────────────────────────────
  // Brick-stagger: insert row-break elements to force alternating row counts.
  // Mobile: 2-3-2-3, Desktop: 3-2-3-2 (centered → bricks land between).
  // ────────────────────────────────────────────────────────────────────────
  function applyBrickStagger(container) {
    if (!container) return;
    // Remove any previous breaks before re-applying.
    container.querySelectorAll('.vq6__brick-break').forEach(function (b) { b.remove(); });

    var pills = Array.prototype.slice.call(container.querySelectorAll('.vq6__pill'));
    if (!pills.length) return;

    var isDesktop = window.matchMedia('(min-width: 750px)').matches;
    var pattern = isDesktop ? [3, 2] : [2, 3];

    var i = 0;
    var row = 0;
    while (i < pills.length) {
      var rowSize = pattern[row % pattern.length];
      var nextI = Math.min(i + rowSize, pills.length);
      // Insert a break AFTER the last pill of this row, unless it's the final row.
      if (nextI < pills.length) {
        var lastPill = pills[nextI - 1];
        var brk = document.createElement('span');
        brk.className = 'vq6__brick-break';
        brk.setAttribute('aria-hidden', 'true');
        lastPill.after(brk);
      }
      i = nextI;
      row += 1;
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // Grey-out: fetch /collections/all?{params}&section_id=cust-v6-filter-data
  // Parse the embedded JSON to map filter param → set of values with count>0.
  // ────────────────────────────────────────────────────────────────────────
  function fetchFilterAvailability(state, sectionId) {
    var qs = buildSRAQuery(state);
    var url = '/collections/all?' + (qs ? qs + '&' : '') + 'section_id=' + encodeURIComponent(sectionId);

    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('SRA fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // The SRA response wraps the section in a <div id="shopify-section-...">.
        // Extract the data-v6-filter-defs <script> element.
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('script[data-v6-filter-defs]');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (e) {
          console.warn('[v6-quiz] filter-defs JSON parse failed:', e);
          return null;
        }
      })
      .catch(function (e) {
        console.warn('[v6-quiz] availability fetch failed:', e);
        return null;
      });
  }

  function applyAvailability(section, defs) {
    if (!defs || !defs.filters) return;

    // Build a map: param → { value → { count, active } }
    var byParam = {};
    defs.filters.forEach(function (f) {
      // Shopify returns filter.param_name like "filter.p.m.custom.shop_by_benefit"
      // Map back to our quiz URL keys.
      var key = mapShopifyParamToQuizKey(f.param);
      if (!key) return;
      byParam[key] = {};
      f.values.forEach(function (v) {
        byParam[key][v.value] = v.count;
      });
    });

    // Walk every option in every step and toggle is-disabled.
    section.querySelectorAll('[data-vq6-options]').forEach(function (opts) {
      var step = opts.getAttribute('data-vq6-options');

      // ──── Hair-likeness trump-card override ────
      // Step 5 (hair likeness) is the trump-card step: NEVER grey out any
      // option, regardless of upstream filter intersection. If the constrained
      // likenesses (Black Coily, Bleached Blonde, Colored) have zero results
      // with the picked upstream filters, navigateToCollection() will detect
      // matched_count===0 at submit time and fall back to hair-likeness-only.
      // This branch only ensures the visual stays clean — clear any prior
      // is-disabled classes so the screen never looks racially exclusionary.
      // DO NOT REMOVE — see project_quiz_no_black_output_rules.md memory.
      if (step === '5') {
        opts.querySelectorAll('[data-filter-key][data-filter-val]').forEach(function (el) {
          el.classList.remove('is-disabled');
        });
        return;
      }

      var paramKey = STEP_PARAM[step];
      if (!paramKey) return;
      var availability = byParam[paramKey];
      if (!availability) return;

      opts.querySelectorAll('[data-filter-key][data-filter-val]').forEach(function (el) {
        // Audience shortcut buttons (Man / Kids / Pet) are always tappable.
        // They redirect immediately and don't depend on the SRA availability
        // payload, which can be unreliable for product_status values when
        // the search index is stale.
        if (el.hasAttribute('data-vq6-shortcut')) {
          el.classList.remove('is-disabled');
          return;
        }
        var val = el.getAttribute('data-filter-val');
        if (!val) return; // empty values (like Woman/Gift) skip greying
        var count = availability[val];
        // Only grey out unselected options. A selected option stays
        // visually selected even if combo currently yields 0 — user can
        // un-tick to recover.
        var selected = el.classList.contains('is-selected');
        if (!selected && (count === 0 || typeof count === 'undefined')) {
          el.classList.add('is-disabled');
        } else {
          el.classList.remove('is-disabled');
        }
      });
    });
  }

  function mapShopifyParamToQuizKey(shopifyParam) {
    if (!shopifyParam) return null;
    // Shopify filter param names look like:
    //   filter.p.m.custom.shop_by_benefit
    //   filter.p.m.custom.shop_by_concern
    //   filter.p.m.custom.scalp_type
    //   filter.p.m.custom.hair_likeness
    //   filter.p.m.custom.shopping_mission
    if (shopifyParam.indexOf('shop_by_benefit') !== -1)   return 'benefit';
    if (shopifyParam.indexOf('shop_by_concern') !== -1)   return 'concern';
    if (shopifyParam.indexOf('scalp_type') !== -1)        return 'scalp_type';
    if (shopifyParam.indexOf('hair_likeness') !== -1)     return 'hair_likeness';
    if (shopifyParam.indexOf('shopping_mission') !== -1)  return 'shopping_mission';
    return null;
  }

  // ────────────────────────────────────────────────────────────────────────
  // Per-section init.
  // ────────────────────────────────────────────────────────────────────────
  function initSection(section) {
    if (!section || initialized.has(section)) return;
    initialized.add(section);

    var sectionId  = section.getAttribute('data-filter-defs-section') || 'cust-v6-filter-data';
    var collectURL = section.getAttribute('data-collection-url') || '/collections/all';

    // Page 6 renders only when capture_enabled. Its presence IS the
    // "capture is on" signal, so unchecking the setting removes the step with
    // no second flag to keep in sync — and setStep's ceiling follows it.
    var captureStepEl = section.querySelector('[data-vq6-capture]');
    var MAX_STEP      = captureStepEl ? 6 : 5;
    var CAPTURE_STEP  = 6;

    // Quiz starts fresh on every visit. Any inherited URL filter params
    // are stripped here so they don't constrain the SRA availability fetch
    // on later pages (which would cause incorrect grey-out). The quiz is
    // an explicit reset point — the URL is rebuilt from selections only.
    var state = {
      shopping_mission: '',
      benefit: [],
      scalp_type: [],
      concern: [],
      hair_likeness: ''
    };
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }

    // hydrateSelections runs on init with empty state, which clears any
    // pre-marked is-selected classes (server-side rendering doesn't apply
    // them, but this is defensive). It also runs again if state is mutated
    // before the user navigates away.
    function hydrateSelections() {
      section.querySelectorAll('[data-filter-key][data-filter-val]').forEach(function (el) {
        var key = el.getAttribute('data-filter-key');
        var val = el.getAttribute('data-filter-val');
        if (!key || !val) return;
        var stateKey = key === 'shopping_mission' ? 'shopping_mission' :
                       key === 'benefit'          ? 'benefit' :
                       key === 'scalp_type'       ? 'scalp_type' :
                       key === 'concern'          ? 'concern' :
                       key === 'hair_likeness'    ? 'hair_likeness' : null;
        if (!stateKey) return;
        var current = state[stateKey];
        var isPicked = Array.isArray(current)
          ? current.indexOf(val) !== -1
          : current === val;
        el.classList.toggle('is-selected', isPicked);
      });
    }

    // ──── Step navigation ────
    var currentStep = 1;
    var dots = section.querySelectorAll('.vq6__dot');

    function setStep(n) {
      n = Math.max(1, Math.min(MAX_STEP, n));
      currentStep = n;
      section.querySelectorAll('.vq6__step').forEach(function (s) {
        var num = parseInt(s.getAttribute('data-vq6-step'), 10);
        s.setAttribute('data-vq6-active', num === n ? 'true' : 'false');
      });
      dots.forEach(function (d) {
        var num = parseInt(d.getAttribute('data-vq6-dot'), 10);
        // The rail renders one dot per real step (six when capture is on), so
        // the active dot maps straight to the step with no clamping.
        d.classList.toggle('is-active', num === n);
      });
      // Intro only on step 1
      var intro = section.querySelector('[data-vq6-intro]');
      if (intro) intro.style.display = (n === 1) ? '' : 'none';

      // Re-apply brick stagger when arriving on a brick page (DOM may have
      // been laid out at a different viewport size when last visited).
      var brickContainer = section.querySelector('[data-vq6-options="' + n + '"].vq6__options--brick');
      if (brickContainer) applyBrickStagger(brickContainer);

      // Refresh availability after every step transition.
      refreshAvailability();

      // Scroll the section into view at the top — important on mobile when
      // the user clicks Next and the new content is below the fold.
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function refreshAvailability() {
      fetchFilterAvailability(state, sectionId).then(function (defs) {
        applyAvailability(section, defs);
      });
    }

    // Debounced refresh — fires 250ms after the last selection click. Long
    // enough to coalesce rapid taps but short enough that the user sees the
    // grey-out reflect their selections before the next pick.
    var refreshTimer = null;
    function scheduleRefresh() {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(function () {
        refreshTimer = null;
        refreshAvailability();
      }, 250);
    }

    // ──── Selection handling ────
    section.querySelectorAll('[data-vq6-pick]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();

        // Disabled options are non-clickable via CSS pointer-events: none,
        // but guard here too in case the class is applied between events.
        if (el.classList.contains('is-disabled')) return;

        var step = parseInt(el.getAttribute('data-vq6-pick'), 10);
        var key = el.getAttribute('data-filter-key');
        var val = el.getAttribute('data-filter-val');

        // ──── Audience shortcut (Man / Child / Pet on Page 1) ────
        // Skip the rest of the quiz and redirect to /collections/all with
        // only the shopping_mission filter applied. Other filter state is
        // dropped intentionally — the audience pick is the entire scope.
        // Shortcuts are not toggleable — clicking redirects. URL uses
        // Shopify-native form so the collection page filters server-side.
        if (el.hasAttribute('data-vq6-shortcut') && val) {
          var shortcutParams = new URLSearchParams();
          shortcutParams.set('filter.p.m.custom.shopping_mission', val);
          window.location.href = collectURL + '?' + shortcutParams.toString();
          return;
        }

        if (SINGLE_STEPS[step]) {
          // Single-select: toggle behavior. Clicking a selected option
          // unselects it and clears the corresponding state key. Clicking
          // a different option clears siblings and selects this one.
          var alreadySelected = el.classList.contains('is-selected');
          var siblings = section.querySelectorAll('[data-vq6-pick="' + step + '"]');
          siblings.forEach(function (s) { s.classList.remove('is-selected'); });

          if (alreadySelected) {
            // Toggle off — clear state, leave nothing selected.
            if (step === 1) state.shopping_mission = '';
            if (step === 5) state.hair_likeness = '';
          } else {
            el.classList.add('is-selected');
            if (!key) {
              // Defensive: a Page-1 button with empty data-filter-key
              // (e.g. a "no preference" option) selects visually but writes
              // nothing. All current Page-1 buttons set shopping_mission,
              // so this branch is reserved for future no-pref additions.
              if (step === 1) state.shopping_mission = '';
              if (step === 5) state.hair_likeness = '';
            } else {
              if (step === 1) state.shopping_mission = val;
              if (step === 5) state.hair_likeness = val;
            }
          }
        } else {
          // Multi-select: toggle.
          var stateKey = key === 'benefit'    ? 'benefit' :
                         key === 'scalp_type' ? 'scalp_type' :
                         key === 'concern'    ? 'concern' : null;
          if (!stateKey) return;
          el.classList.toggle('is-selected');
          var arr = state[stateKey];
          var idx = arr.indexOf(val);
          if (el.classList.contains('is-selected') && idx === -1) arr.push(val);
          else if (!el.classList.contains('is-selected') && idx !== -1) arr.splice(idx, 1);
        }

        writeURLParams(state);
        // Refresh grey-out after every selection (debounced 250ms) so users
        // see options narrow as they pick. Without this the available set
        // only updates on step transition, which feels broken.
        scheduleRefresh();
      });
    });

    // ──── Why we ask reveal ────
    section.querySelectorAll('[data-vq6-why]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = btn.getAttribute('data-vq6-why');
        var body = section.querySelector('[data-vq6-why-body="' + step + '"]');
        if (!body) return;
        if (body.hasAttribute('hidden')) body.removeAttribute('hidden');
        else body.setAttribute('hidden', '');
      });
    });

    // ──── Nav buttons ────
    section.querySelectorAll('[data-vq6-next]').forEach(function (btn) {
      btn.addEventListener('click', function () { setStep(currentStep + 1); });
    });
    section.querySelectorAll('[data-vq6-back]').forEach(function (btn) {
      btn.addEventListener('click', function () { setStep(currentStep - 1); });
    });
    // Pre-flight check before redirect.
    //
    // Layered fallback (see project_quiz_no_black_output_rules.md memory):
    //   1. If the full filter combo yields zero AND a hair_likeness is picked,
    //      drop every other filter and route with hair_likeness ONLY. This is
    //      the trump-card override that prevents Black Coily / Bleached Blonde
    //      / Colored Hair from feeling singled-out by an empty grid.
    //   2. If zero AND no hair_likeness picked, fall back to Most Popular.
    //   3. Otherwise route normally with the full filter set.
    //
    // DO NOT collapse this back into a flat "matched_count===0 → Most Popular"
    // branch. That destroys the trump card and was explicitly rejected by Bryce
    // on 2026-05-01 and re-rejected on 2026-05-06 after a regression.
    var FALLBACK_SORT = 'Most Popular';

    // Stamp ?quiz_complete=1 on the destination when the visitor got here
    // by pressing Submit (not Skip, not the audience shortcut). The filter
    // controller reads this param on the landing page and mirrors it into
    // the _quiz_complete cart attribute.
    function withQuizComplete(url, fromSubmit) {
      if (!fromSubmit) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'quiz_complete=1';
    }

    function navigateToCollection(fromSubmit) {
      var qs = buildFilterQuery(state);
      if (!qs) {
        window.location.href = withQuizComplete(collectURL, fromSubmit);
        return;
      }
      fetchFilterAvailability(state, sectionId).then(function (defs) {
        if (defs && typeof defs.matched_count === 'number' && defs.matched_count === 0) {
          if (state.hair_likeness) {
            // Trump card — strip all upstream filters, keep ONLY hair_likeness.
            // Semantic-key form ?hair_likeness=… is what the V6 collection
            // controller hydrates from. The collection page renders whatever
            // products carry that hair_likeness tag, regardless of the prior
            // scalp / concern / benefit / shopping_mission picks.
            var trump = new URLSearchParams();
            trump.set('hair_likeness', state.hair_likeness);
            window.location.href = withQuizComplete(collectURL + '?' + trump.toString(), fromSubmit);
          } else {
            // No hair_likeness picked yet but combo yields zero — universal
            // safety net: sort_by=Most Popular so something useful renders.
            window.location.href = withQuizComplete(collectURL + '?sort_by=' + encodeURIComponent(FALLBACK_SORT), fromSubmit);
          }
        } else {
          window.location.href = withQuizComplete(collectURL + '?' + qs, fromSubmit);
        }
      }).catch(function () {
        // SRA failed — proceed with user's selections. Better to show
        // possibly-empty results than to block the user behind a hung fetch.
        window.location.href = withQuizComplete(collectURL + '?' + qs, fromSubmit);
      });
    }

    // Skip is NOT completion — no quiz_complete stamp, no Klaviyo event.
    section.querySelectorAll('[data-vq6-skip]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        navigateToCollection(false);
      });
    });
    section.querySelectorAll('[data-vq6-submit]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Fire the onsite event BEFORE navigating. The pre-flight SRA fetch
        // inside navigateToCollection() gives the track request time to land
        // before the page unloads. Wrapped so a Klaviyo failure (blocked
        // script, no window.klaviyo) can never block the redirect.
        try {
          window.klaviyo = window.klaviyo || [];
          window.klaviyo.push(['track', 'Completed Quiz', {
            shopping_mission: state.shopping_mission || null,
            benefits: (state.benefit || []).join(', ') || null,
            scalp_types: (state.scalp_type || []).join(', ') || null,
            concerns: (state.concern || []).join(', ') || null,
            hair_likeness: state.hair_likeness || null,
            source: 'find_your_viori'
          }]);
        } catch (_) { /* non-blocking */ }
        // The quiz is complete either way — the Completed Quiz event above
        // already fired. Capture is an extra step before the redirect, never
        // a gate on it.
        if (captureStepEl && !alreadyCaptured()) { setStep(CAPTURE_STEP); return; }
        navigateToCollection(true);
      });
    });

    // ──── Capture step (page 6) ────
    var capEls = captureStepEl ? {
      input:      captureStepEl.querySelector('[data-vq6-capture-input]'),
      error:      captureStepEl.querySelector('[data-vq6-capture-error]'),
      consent:    captureStepEl.querySelector('[data-vq6-capture-consent]'),
      consentBox: captureStepEl.querySelector('[data-vq6-capture-consent-input]'),
      submit:     captureStepEl.querySelector('[data-vq6-capture-submit]'),
      skip:       captureStepEl.querySelector('[data-vq6-capture-skip]')
    } : null;

    function showCaptureError(msg) {
      if (!capEls || !capEls.error) return;
      capEls.error.textContent = msg;
      capEls.error.hidden = false;
      if (capEls.input) capEls.input.classList.add('is-invalid');
    }
    function clearCaptureError() {
      if (!capEls || !capEls.error) return;
      capEls.error.textContent = '';
      capEls.error.hidden = true;
      if (capEls.input) capEls.input.classList.remove('is-invalid');
    }
    // Reveal the consent block only once the value parses as a phone number.
    function syncConsentVisibility() {
      if (!capEls || !capEls.input || !capEls.consent) return;
      var raw = (capEls.input.value || '').trim();
      capEls.consent.hidden = !(!isEmail(raw) && !!normalizePhone(raw));
    }

    // The five quiz answers, prefixed and CSV-joined. Keys mirror the state
    // keys so a property maps back to the question that produced it.
    function buildCaptureProperties() {
      var attribution = {};
      try {
        if (window.VioriAttribution && typeof window.VioriAttribution.snapshot === 'function') {
          attribution = window.VioriAttribution.snapshot() || {};
        }
      } catch (e) { /* noop */ }

      var filters = {};
      try {
        filters = flattenFilters(window.VioriFilters && window.VioriFilters.getState());
      } catch (e) { /* noop */ }

      var merged = Object.assign(
        {},
        attribution,
        {
          capture_surface:       'v6_quiz',
          quiz_shopping_mission: state.shopping_mission || '',
          quiz_benefit:          (state.benefit || []).join(','),
          quiz_scalp_type:       (state.scalp_type || []).join(','),
          quiz_concern:          (state.concern || []).join(','),
          quiz_hair_likeness:    state.hair_likeness || '',
          quiz_completed_at:     new Date().toISOString(),
          capture_page_path:     window.location.pathname
        },
        filters
      );

      var out = {};
      Object.keys(merged).forEach(function (k) { putProp(out, k, merged[k]); });
      return out;
    }

    function submitCapture() {
      if (!capEls || !capEls.input) return navigateToCollection(true);
      clearCaptureError();

      var raw = (capEls.input.value || '').trim();
      var email = null;
      var phone = null;
      if (isEmail(raw)) { email = raw; } else { phone = normalizePhone(raw); }

      if (!email && !phone) {
        showCaptureError('Enter a valid email address or phone number.');
        return;                                   // keep the value, do not redirect
      }
      // An SMS subscription may only ever follow an explicit tick.
      if (phone && !(capEls.consentBox && capEls.consentBox.checked)) {
        showCaptureError('Please agree to receive texts, or enter an email instead.');
        return;
      }

      var company = captureStepEl.getAttribute('data-klaviyo-company') || '';
      var listId  = phone
        ? (captureStepEl.getAttribute('data-klaviyo-sms-list') || '')
        : (captureStepEl.getAttribute('data-klaviyo-email-list') || '');

      var attrs = {};
      if (email) {
        attrs.email = email;
        attrs.subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      } else {
        attrs.phone_number = phone;
        attrs.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };
      }
      var props = buildCaptureProperties();
      if (Object.keys(props).length) attrs.properties = props;

      if (capEls.submit) {
        capEls.submit.disabled = true;
        capEls.submit.textContent = 'Sending…';
      }

      postToKlaviyoWithRetry(company, listId, attrs)
        .then(function () {
          // Only a confirmed 2xx marks the visitor captured. A failed call
          // must not suppress the step forever — that loses the lead twice.
          markCaptured();
          trackCapture('quiz_email_capture', { method: phone ? 'sms' : 'email', quiz: 'v6_quiz' });
          // Newsletter (master) list — EMAIL path only, fire-and-forget. The
          // quiz list alone triggers no flow; this is what starts the Prospects
          // welcome flow. Same `attrs`, so attribution properties carry over.
          // Never awaited: the redirect to results must not wait on it, and a
          // failure here must not undo markCaptured() or the navigation. The
          // SMS path is left alone — it already posts to its own SMS list.
          var master = captureStepEl.getAttribute('data-klaviyo-master-list') || '';
          if (!phone && master && master !== listId) {
            postToKlaviyoWithRetry(company, master, attrs)
              .catch(function (err) { console.warn('[v6-quiz] master list POST failed:', err); });
          }
        })
        .catch(function (err) {
          console.warn('[v6-quiz] capture failed after retry — continuing to results:', err);
        })
        .then(function () {
          writeCartAttributes();          // fire and forget, keepalive
          navigateToCollection(true);     // the trip always happens
        });
    }

    function skipCapture() {
      // No flag: skipping is not capturing, so the step may appear again.
      // Still a completed quiz, so the quiz_complete stamp is preserved.
      trackCapture('quiz_capture_skip', { quiz: 'v6_quiz' });
      navigateToCollection(true);
    }

    if (capEls) {
      if (capEls.submit) capEls.submit.addEventListener('click', function (e) { e.preventDefault(); submitCapture(); });
      if (capEls.skip)   capEls.skip.addEventListener('click',   function (e) { e.preventDefault(); skipCapture(); });
      if (capEls.input)  capEls.input.addEventListener('input',  function () { clearCaptureError(); syncConsentVisibility(); });
      if (capEls.consentBox) capEls.consentBox.addEventListener('change', clearCaptureError);
    }

    // ──── Brick stagger on initial load + on resize ────
    section.querySelectorAll('.vq6__options--brick').forEach(function (c) {
      applyBrickStagger(c);
    });
    var resizeT = null;
    window.addEventListener('resize', function () {
      if (resizeT) clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        section.querySelectorAll('.vq6__options--brick').forEach(function (c) {
          applyBrickStagger(c);
        });
      }, 200);
    });

    // ──── Hydrate selection state from URL on init ────
    hydrateSelections();
    // Initial availability fetch
    refreshAvailability();
  }

  function initAll() {
    document.querySelectorAll('#cust-v6-quiz').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    var node = e.target;
    if (!node) return;
    var sec = node.querySelector ? node.querySelector('#cust-v6-quiz') : null;
    if (sec) initSection(sec);
  });
})();
